<template>
    <CardContentPanel icon="warning-triangle" v-bind="{action, index}">
        <template #title>{{ $t('projectclient.projectview.activity.pending.actions.EXPENSE_PAYMENT_OVERDUE.title') }}</template>
        <template #body>
            {{ getLabel }}
        </template>
        <template #actions>
            <NuxtLink
                :to="`/project-payment/${action.extraData.expenseApprovalSetId}?origin=project-view`"
                external
                data-testid="pending-action-card-cta-primary"
            >
                <VJoyButton variant="main" size="small">
                    {{ $t('projectclient.projectview.activity.pending.actions.EXPENSE_PAYMENT_OVERDUE.action.primary') }}
                </VJoyButton>
            </NuxtLink>
        </template>
    </CardContentPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {parse} from 'tinyduration';
    import {useLogger, useTranslation, useFormat} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import {useProjectInformationStore} from '~/common/stores';
    import CardContentPanel from '../CardContentPanel.vue';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const props = defineProps<{
        action: ActivityPendingActionByType<'EXPENSE_PAYMENT_OVERDUE'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const logger = useLogger();
    const {t} = useTranslation();
    const {formatAmount} = useFormat();

    const calculatePastDue = computed<number>(() => {
        if (props.action.extraData.dueSince) {
            return Math.abs(parse(props.action.extraData.dueSince).days ?? 0);
        }
        logger.error(
            `Missing "dueSince" extraData from EXPENSE_PAYMENT_OVERDUE pending action in project ${projectInformationStore.projectInformation.projectId}`,
        );
        return 0;
    });

    const formattedAmount = computed<string>(() => {
        const amount = props.action.extraData.remaining;
        return !isNaN(amount) ? formatAmount(projectInformationStore.projectInformation.contract.currency, amount, {decimals: 2}) : '-';
    });

    const getLabel = computed(() => {
        const {extraData} = props.action;
        const translationKey = `projectclient.projectview.activity.pending.actions.EXPENSE_PAYMENT_OVERDUE.description`;
        if (typeof extraData.billingContact === 'undefined') {
            return t(translationKey, {dueSince: calculatePastDue.value});
        } else {
            return t(`${translationKey}.with.billing.email`, {
                dueSince: calculatePastDue.value,
                billingEmail: extraData.billingContact,
            });
        }
    });
</script>
